import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

import {
  Link,
  Divider,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Grid,
  Dialog,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Image, NotificationBanner, Translate } from '@hydra/common/components';
import UpdatePaymentDialog from '@hydra/common/components/UpdatePaymentDialog';
import CvvVerificationDialog from '@hydra/common/components/CvvVerificationDialog';
import { useUserContext } from '@hydra/common/context';

import { getCardInfo, getRecurlySubscription } from '@hydra/utils/accountUtil';
import * as fetcher from '@hydra/utils/fetcher';
import { sendEvent } from '@hydra/utils/gtm';
import logger from '@hydra/utils/logger';
import { getHelpLink } from '@hydra/utils/getHelpLink';

import { usePricingRoutes, useI18n } from '@hydra/common/customHooks';

import StartSubscriptionBlue from '@hydra/assets/img/start-subscription-blue.svg';
import StartSubscriptionConfirmation from '@hydra/assets/img/start-subscription-confirmation.svg';

const useStyles = makeStyles(theme => ({
  dialog: { maxWidth: '650px' },
  dialogTitle: { textAlign: 'center' },
  title: {
    fontWeight: theme.typography.fontWeightSemibold,
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5)
  },
  titleWidth: { maxWidth: '340px' },
  titleConfirmation: {
    fontWeight: theme.typography.fontWeightSemibold,
    marginBottom: theme.spacing(1.5)
  },
  content: {
    textAlign: 'center',
    padding: theme.spacing(5, 0)
  },
  svgContainer: {
    margin: '0 auto',
    width: 250,
    height: 190,
    position: 'relative'
  },
  svg: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  subTitle: { fontSize: theme.typography.fontSizeRoot },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  divider: {
    backgroundColor: theme.palette.grey[200]
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  leftText: {
    fontSize: theme.typography.fontSizeRoot,
    textAlign: 'left'
  },
  rightText: {
    fontSize: theme.typography.fontSizeRoot,
    fontWeight: theme.typography.fontWeightBold
  },
  link: {
    fontSize: theme.typography.fontSizeLarge
  },
  alignLeft: {
    textAlign: 'left'
  },
  maxWidth: { maxWidth: '80%' }
}));

export const TRIAL_MAX_REVIEW_CALLS = 5;

export const signConversion = {
  EUR: '€',
  GBP: '£',
  USD: 'US$'
};

interface ITrialLimitDialog {
  open: boolean,
  onClose: () => void,
  preventClose?: boolean
}

interface IPlan {
  active?: boolean,
  allowedProjects?: number,
  allowedReports?: number,
  allowedSubusers?: number,
  billingCycle?: string,
  id?: number
  maxCrawlCredits?: number,
  maxKeywords?: number,
  planCode?: string,
  price?: string
  priceEur?: string
  priceGbp?: string
  priceUsd?: string
}

interface ISignConversion {
  EUR: '€',
  GBP: '£',
  USD: 'US$'
}
const defaultCurrency = 'USD';

export const TrialLimitDialog = ({ open, onClose, preventClose = false }: ITrialLimitDialog) => {
  const { locale } = useI18n();
  const classes = useStyles();
  const { user } = useUserContext();

  const plan: IPlan = (user && user.subscription && user.subscription.plan) || {};
  const currency = user && Object.keys(signConversion).includes(user.currency) ? user.currency : defaultCurrency;
  const price = user && plan[`price${currency.charAt(0).toUpperCase() + currency.slice(1).toLowerCase()}` as keyof IPlan];
  const sign = user && signConversion[`${currency}` as keyof ISignConversion];

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [fail, setFail] = useState(false);
  const [isCvvDialogOpen, setIsCvvDialogOpen] = useState(false);
  const [activationError, setActivationError] = useState(false);
  const [cvvError, setCvvError] = useState(false);
  const [cardInfo, setCardInfo] = useState('card on file');
  const { getPricingPageRoute } = usePricingRoutes();
  const hrefLink = getHelpLink(locale === 'de' || locale === 'nl' ? 'en' : locale, '/articles/360000136505-How-Do-You-Handle-VAT-');

  useEffect(() => {
    return () => setConfirmation(false);
  }, []);

  useEffect(() => {
    if (confirmation) {
      sendEvent('activate-trial-popup-success-open');
    }
  }, [confirmation]);

  useEffect(() => {
    if (open) {
      sendEvent('activate-trial-popup-open');
      getRecurlySubscription()
        .then(subscription => setCardInfo(getCardInfo(subscription)))
        .catch(() => {}); // no subscription yet - keep the 'card on file' fallback
    }
    setIsOpen(open || false);
  }, [open]);

  const handleActivateTrial = async (recurlyToken: string) => {
    sendEvent('activate-trial-popup-start-btn');
    setLoading(true);
    setActivationError(false);
    setCvvError(false);
    try {
      const { error } = await fetcher.del('/api/user/trial', { json: { recurlyToken } }).json();
      setLoading(false);
      setIsCvvDialogOpen(false);
      if (error) {
        onClose();
        setFail(true);
      } else {
        setConfirmation(true);
      }
    } catch (err) {
      logger.error({
        event: 'activate-trial-error',
        err
      });
      setLoading(false);
      setIsCvvDialogOpen(false);
      if (err instanceof fetcher.HTTPError && err.response.status === 402) {
        setCvvError(true);
      } else {
        setActivationError(true);
      }
    }
  };

  const handleCloseBtn = () => {
    if (!preventClose) {
      return onClose();
    }

    window.location.href = `/${locale}/user/account?activateTrialSuccess=1`;
  };

  return (
    <>
      <UpdatePaymentDialog open={fail} preventClose={preventClose} />
      <Dialog
        fullWidth
        maxWidth='sm'
        open={isOpen}
        onClose={() => {
          if (!preventClose) {
            onClose();
          }
        }}
        classes={{
          paperWidthSm: classes.dialog
        }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          {!preventClose && (
            <IconButton aria-label='close' className={classes.closeButton} onClick={onClose}>
              <Icon type='cross' />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent className={classes.content}>
          {confirmation
            ? (
              <>
                <div className={classes.svgContainer}>
                  <Image src={StartSubscriptionConfirmation} className={classes.svg} />
                </div>
                <Typography variant='h3' component='h3' gutterBottom className={classes.titleConfirmation}>
                  <Translate content='trial_review_limit_dialog_no_longer_trial' />
                </Typography>
                <Grid container justify='center'>
                  <Grid item xs={10} className={classes.marginBottom}>
                    <Typography paragraph color='textSecondary'>
                      <Translate content='trial_review_limit_dialog_close_popup' />
                    </Typography>
                  </Grid>
                  <Grid item xs={10} className={classes.marginBottom}>
                    <Button
                      fullWidth
                      size='large'
                      color='primary'
                      variant='contained'
                      onClick={handleCloseBtn}
                    >
                      <Translate content='trial_review_limit_dialog_close_btn' />
                    </Button>
                  </Grid>
                </Grid>
              </>
              )
            : (
              <>
                {(activationError || cvvError) && (
                  <Grid container justify='center'>
                    <Grid item xs={10} className={classes.marginBottom}>
                      <NotificationBanner
                        severity='error'
                        title={<Translate content={cvvError ? 'cvv_verification_failed' : 'error_500'} />}
                      />
                    </Grid>
                  </Grid>
                )}
                <div className={classes.svgContainer}>
                  <Image src={StartSubscriptionBlue} className={classes.svg} />
                </div>
                <Typography variant='h3' component='h3' className={classes.title}>
                  <Translate content='trial_review_limit_dialog_title' component='div' className={classes.titleWidth} />
                </Typography>
                <Grid container justify='center'>
                  <Grid item xs={10} className={classes.marginBottom}>
                    <Translate
                      content='trial_review_limit_dialog_subtitle_max_reviews'
                      with={{ maxReviews: TRIAL_MAX_REVIEW_CALLS }}
                      className={classes.subTitle}
                    />
                  </Grid>
                  <Grid item xs={10} className={classes.marginBottom}>
                    <Divider className={classnames(classes.divider, classes.marginBottom)} />
                    <Grid container justify='space-between' className={classes.marginBottom}>
                      <Grid item className={classes.leftText}><Translate content='trial_review_limit_dialog_selected_plan' /></Grid>
                      <Grid item className={classes.rightText}>
                        <Translate content={plan.planCode && plan.planCode.includes('pro') ? 'trial_review_limit_dialog_pro_planname' : 'trial_review_limit_dialog_premium_planname'} />
                      </Grid>
                    </Grid>
                    <Grid container justify='space-between' className={classes.marginBottom}>
                      <Grid item className={classes.leftText}>
                        <Translate content='trial_review_limit_dialog_period' component='div' className={classes.alignLeft} />
                        {plan.billingCycle === 'month' && (
                          <Link href={getPricingPageRoute({ hash: 'yearly' })}>
                            <Translate content='trial_review_limit_dialog_save_yearly' component='div' />
                          </Link>
                        )}
                      </Grid>
                      <Grid item className={classes.rightText}>
                        {plan.billingCycle === 'year'
                          ? <Translate content='trial_review_limit_dialog_yearly' />
                          : <Translate content='trial_review_limit_dialog_monthly' />}
                      </Grid>
                    </Grid>
                    <Grid container justify='space-between' className={classes.marginBottom}>
                      <Grid item className={classnames(classes.leftText, classes.maxWidth)}>
                        <Translate content='trial_review_limit_dialog_total_due' component='div' className={classes.alignLeft} />
                        <Link href={hrefLink}>
                          <Translate content='trial_review_limit_dialog_prices_vary' />
                        </Link>
                      </Grid>
                      <Grid item className={classes.rightText}>{`${sign}${price}`}</Grid>
                    </Grid>
                    <Divider className={classes.divider} />
                  </Grid>
                  <Grid item xs={10} className={classes.marginBottom}>
                    <CvvVerificationDialog
                      isOpen={isCvvDialogOpen}
                      titleContent='trial_review_limit_dialog_cvv_title'
                      descriptionContent='trial_review_limit_dialog_cvv_description'
                      descriptionWith={{ cardInfo }}
                      onClose={() => setIsCvvDialogOpen(false)}
                      onToken={handleActivateTrial}
                    />
                    <Button
                      fullWidth
                      size='large'
                      color='primary'
                      variant='contained'
                      onClick={() => setIsCvvDialogOpen(true)}
                      startIcon={loading && <Icon type='loader' color='grey' size='sm' />}
                    >
                      <Translate content='trial_review_limit_dialog_btn' />
                    </Button>
                  </Grid>
                  <Grid item xs={10}>
                    <Link
                      href={getPricingPageRoute()}
                      className={classes.link}
                      onClick={() => sendEvent('activate-trial-popup-edit-btn')}
                    >
                      <Translate content='trial_review_limit_dialog_subbtn' />
                    </Link>
                  </Grid>
                </Grid>
              </>
              )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default React.memo(TrialLimitDialog);
